import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, Section, ReqTable } from "./LegalLayout";

const TermsEn = () => (
  <LegalLayout
    title="Terms of Service"
    updated="June 14, 2026"
    backLabel="← Back to Home"
    updatedPrefix="Updated"
  >
    <p style={{ fontSize: "0.88rem", color: "rgba(0,0,0,0.55)", marginBottom: "2rem" }}>
      Service Provider: Sole trader KOKOV ALEKSANDR DMITRIEVICH, OGRNIP:
      325784700333162, INN: 784302022304, address: Russia, Saint Petersburg,
      Komarovo, Tsvetochnaya 2A
    </p>

    <Section title="1. General Provisions">
      <p>
        This document constitutes a public offer (hereinafter — "Agreement") in
        accordance with Articles 435–444 of the Civil Code of the Russian
        Federation. Payment for the service constitutes acceptance of this
        Agreement. Acceptance means full agreement with its terms.
      </p>
      <p>
        The Service Provider reserves the right to unilaterally amend the terms
        of this Agreement by publishing a new version on the website{" "}
        <strong>swingmentor.pro</strong>. Amendments do not apply to services
        that have already been paid for.
      </p>
    </Section>

    <Section title="2. Subject of Agreement">
      <p>
        The Service Provider grants the Customer access to the online service{" "}
        <strong>SwingMentor</strong> (website <strong>swingmentor.pro</strong>) —
        an automated golf swing analysis service that uses artificial intelligence
        to analyse uploaded videos. Under the purchased plan, the Customer
        receives:
      </p>
      <ul>
        <li>the ability to upload swing videos and have them automatically analysed;</li>
        <li>personalised technique recommendations based on the analysis;</li>
        <li>a credit package for analysis according to the chosen plan;</li>
        <li>access to their analysis history in their personal account;</li>
        <li>access to AI mentor features (AI coaching).</li>
      </ul>
      <p>
        A credit is an internal unit of account deducted when an analysis is
        performed. New users receive 30 credits free of charge.
      </p>
    </Section>

    <Section title="3. Pricing and Payment">
      <ReqTable
        rows={[
          ["SwingMentor Pro — 1 month", "899 ₽ (500 credits)"],
          ["SwingMentor Pro — 1 year", "8,990 ₽ (6,000 credits)"],
          ["SwingMentor Premium — 1 month", "1,799 ₽ (1,500 credits)"],
          ["SwingMentor Premium — 1 year", "17,990 ₽ (18,000 credits)"],
        ]}
      />
      <p>
        VAT is not applicable as the Service Provider operates under the
        simplified tax system. For users in Russia, payment is made as a one-time
        charge by bank card via YooKassa (YooMoney NKCO LLC). International
        users may pay by cryptocurrency through Trybit. Automatic renewal and
        recurring charges do not apply: upon expiry of the paid period, access to
        the plan ends and the account reverts to the free plan.
      </p>
      <p>
        Plan access and credits are granted automatically upon payment
        confirmation.
      </p>
    </Section>

    <Section title="4. Refund Policy">
      <p>
        The Customer may cancel the service and receive a full refund if the
        credits of the paid plan <strong>have not been used</strong> (no analysis
        has been performed against the paid package). To request a refund, send a
        request to{" "}
        <a href="mailto:info@swingmentor.pro">info@swingmentor.pro</a> within 14
        days of the payment date.
      </p>
      <p>
        After the paid package has been partially used, a refund may be issued
        on a pro-rata basis for the unused portion of the period and credits,
        less any costs actually incurred by the Service Provider. Requests are
        processed within 10 business days.
      </p>
    </Section>

    <Section title="5. Rights and Obligations">
      <p><strong>The Customer undertakes to:</strong></p>
      <ul>
        <li>provide accurate information during registration and payment;</li>
        <li>
          upload only videos to which they hold lawful rights and for which they
          have the consent of any individuals depicted;
        </li>
        <li>not upload material that violates applicable law;</li>
        <li>not share account access with third parties.</li>
      </ul>
      <p><strong>The Service Provider undertakes to:</strong></p>
      <ul>
        <li>
          ensure service availability of at least 99% of the time (excluding
          scheduled maintenance);
        </li>
        <li>
          ensure the confidentiality of personal data in accordance with the{" "}
          <Link to="/legal/privacy-en">Privacy Policy</Link>;
        </li>
        <li>credit the purchased credits in full upon payment confirmation.</li>
      </ul>
    </Section>

    <Section title="6. Content and Intellectual Property">
      <p>
        Videos uploaded by the Customer remain their property. The Service
        Provider receives a non-exclusive licence to use them solely for the
        purpose of providing the service (performing the analysis).
      </p>
      <p>
        The original video and any temporary files created during processing are
        retained only for the duration of the analysis, including any provided
        retry attempts, and are deleted after successful completion or final
        failure of the job. The temporary copy on an AI node is deleted after
        each processing attempt completes.
      </p>
      <p>
        The processed video is saved in the account history only when the user
        has enabled the video saving setting. It is retained until that setting
        is disabled or the account is deleted. Text analysis results, scores,
        recommendations, and phase images are stored in history until the account
        is deleted or a confirmed deletion request is fulfilled. Data the Service
        Provider is required to retain by law is not subject to deletion until
        the mandatory retention period has expired.
      </p>
      <p>
        Analysis results are advisory in nature, do not constitute medical or
        professional coaching advice, and do not guarantee any sporting
        performance outcomes.
      </p>
    </Section>

    <Section title="7. Liability">
      <p>
        The Service Provider's liability under this Agreement is limited to the
        amount paid by the Customer for the service. The Service Provider is not
        liable for the operation of third-party services (payment systems,
        network providers) or technical failures beyond its control.
      </p>
    </Section>

    <Section title="8. Service Provider Details">
      <ReqTable
        rows={[
          ["Service Provider", "Sole trader Kokov Aleksandr Dmitrievich"],
          ["OGRNIP", "325784700333162"],
          ["INN", "784302022304"],
          ["Address", "Russia, Saint Petersburg, Komarovo, Tsvetochnaya 2A"],
          ["Email", "info@swingmentor.pro"],
          ["Phone", "+7 999 032-27-57"],
          ["Working hours", "Mon–Fri, 10:00–19:00 MSK"],
        ]}
      />
    </Section>

    <Section title="9. Personal Data Notice">
      <p>
        During registration, payment, and video upload, personal data is
        processed in accordance with the{" "}
        <Link to="/legal/privacy-en">Privacy Policy</Link> and the{" "}
        <Link to="/legal/consent-en">Personal Data Processing Consent</Link>.
      </p>
      <p>
        For users from Russia, primary data storage occurs in databases located
        in the Russian Federation. For users from other countries, data is stored
        on a server in Germany (EU). For video analysis, a temporary copy of the
        video may be transmitted to an AI infrastructure node, without the
        Customer's name, email address, or payment details. The temporary copy
        is deleted after the processing attempt completes.
      </p>
    </Section>

    <Section title="10. Dispute Resolution">
      <p>
        Disputes shall be resolved through negotiation. If no agreement is
        reached, disputes shall be submitted to the court at the Service
        Provider's location (if the Customer is a legal entity) or at the
        Customer's place of residence (if the Customer is a consumer).
      </p>
    </Section>
  </LegalLayout>
);

export default TermsEn;
